/**
 * Chart documentation shown behind the info icon on the Meta Engagement
 * dashboard.
 *
 * Keys match the chart link property names in `MopaMetaEngagementComponent`.
 */
export const MopaMetaEngagementChartDocs = {
    dau: /* HTML */ `<p>
            Distribution of active users for each day, split into META / HOPA /
            CORE LOOP / META Prod. user shares over time.
        </p>
        <p>
            <b>All</b> = everyone active that day; <b>META</b> = opened the deco
            menu; <b>HOPA</b> = spent &ge;1 energy; <b>META Prod.</b> = placed
            &ge;1 decoration (excluding move/redo); <b>CORE LOOP</b> = HOPA and
            META on the same day.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only). Always daily - the date aggregation setting does not apply.
        </p>`,

    activity_share_by_day_no: /* HTML */ `<p>
            Share of active users on a given day of the player's life, split
            into META / HOPA / CORE LOOP / META Prod. users - a player can
            belong to several categories at once, so the lines don't sum to
            100%.
        </p>
        <p>
            Definitions: see the DAU chart. Install day = calendar days since
            install (day 0 = install); active day = the player's n-th active
            day. Each point counts only players active on that day-number.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only).
        </p>`,

    median_lifetime_days_share_by_day_no: /* HTML */ `<p>
            Median % of days spent as a given user type up to that day of the
            player's life - e.g. 50% on day d9 means that the player belonged to
            that category for 5 out of 10 days.
        </p>
        <p>
            Per player: running count of days as the type divided by days
            elapsed (day-number + 1); median across users active on that
            day-number (no carry-forward on inactive days). <b>All</b> = share
            of calendar days the player was active at all - the ceiling for the
            other lines.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only).
        </p>`,

    avg_lifetime_days_share_by_day_no: /* HTML */ `<p>
            Average % of days spent as a given user type up to that day of the
            player's life - the average counterpart of the median chart.
        </p>
        <p>
            Per player: running count of days spent as the given user type
            divided by days elapsed (day-number + 1), averaged across users
            active on that day-number.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only).
        </p>`,

    median_meta_time_share_by_day_no: /* HTML */ `<p>
            Median share of session time spent in META, computed per user on a
            given day of the player's life and aggregated over active users.
        </p>
        <p>
            META time = decorating (closed DesignMode flows); denominator =
            client-reported total play time . Only measured players enter - no
            ctx (Player context) data drops out; Sample (n) in the tooltip.
            RAW/uncapped - can exceed 100%; ctx is reported from build 4.0.100.
            Independent of the user-type checkboxes.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only).
        </p>`,

    avg_meta_time_share_by_day_no: /* HTML */ `<p>
            Average share of session time spent in META, computed per user on a
            given day of the player's life and averaged over active users.
        </p>
        <p>
            META time = decorating (closed DesignMode flows); denominator =
            client-reported total play time ; measured players only; Sample (n)
            in the tooltip. RAW/uncapped and outlier-sensitive - single extreme
            shares (&gt;100%) can spike the average. Independent of the
            user-type checkboxes.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only).
        </p>`,

    median_hopa_time_share_by_day_no: /* HTML */ `<p>
            Median HOPA Daily Time Share per active user.
        </p>
        <p>
            Formula =
            <code
                >(ctx_total_time_daily_min - meta_time_min - shop_time_min) /
                ctx_total_time_daily_min</code
            >. META and Shop time are measured from their respective closed flow
            events; HOPA is what is left of the day. The denominator is the
            client total-time counter - the same one META Share and Shop Share
            use, so the three shares add up to 100% for every player-day.
        </p>
        <p>
            Until the Shop flow events ship (client 4.1.100) Shop time counts as
            0, so those minutes sit inside HOPA here. Version-gated with the
            counter: NULL before 4.0.100.
        </p>
        <p>
            The raw result is not capped. Values below 0% or above 100% remain
            visible as data-quality signals. Sample (n) in the tooltip counts
            active users with a non-null HOPA Daily Time Share.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only).
        </p>`,

    avg_hopa_time_share_by_day_no: /* HTML */ `<p>
            Average HOPA Daily Time Share per active user.
        </p>
        <p>
            Formula =
            <code
                >(ctx_total_time_daily_min - meta_time_min - shop_time_min) /
                ctx_total_time_daily_min</code
            >. META and Shop time are measured from their respective closed flow
            events; HOPA is what is left of the day, over the same client
            total-time denominator META Share and Shop Share use.
        </p>
        <p>
            Until the Shop flow events ship (client 4.1.100) Shop time counts as
            0, so those minutes sit inside HOPA here. Version-gated with the
            counter: NULL before 4.0.100.
        </p>
        <p>
            The raw result is not capped, so outliers and values outside 0-100%
            remain visible. The mean can be particularly sensitive to those
            cases; use Sample (n) in the tooltip for context.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only).
        </p>`,

    median_hopa_time_share_by_session_by_day_no: /* HTML */ `<p>
            Median event-context HOPA Daily Time Share per active user.
        </p>
        <p>
            Formula = <code>adventure_time_min / project_session_time_min</code
            >. Both values come from <code>project_sessions</code>, so this is
            the share of engaged event time spent in a real adventure, rather
            than a residual of non-adventure flows.
        </p>
        <p>
            The raw result is not capped. Values above 100% remain visible as
            data-quality signals. This is independent of the user-type
            checkboxes. Sample (n) in the tooltip counts active users with both
            event time inputs available.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only).
        </p>`,

    avg_hopa_time_share_by_session_by_day_no: /* HTML */ `<p>
            Average counterpart of the project-session HOPA Daily Time Share. It
            is computed per active user as adventure event time divided by
            engaged project-session time, then averaged.
        </p>
        <p>
            Formula = <code>adventure_time_min / project_session_time_min</code
            >; individual shares are raw and uncapped, so values above 100%
            remain visible as data-quality signals. It does not use wall-clock
            <code>session_time_min</code>, whose very small values made an
            adventure/session average unstable.
        </p>
        <p>
            This is independent of the user-type checkboxes. Sample (n) in the
            tooltip counts active users with both event time inputs available.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only).
        </p>`,

    median_shop_time_share_by_day_no: /* HTML */ `<p>
            Median Shop Daily Time Share: the share of the day spent browsing
            the shop, computed per user on a given day of the player's life and
            aggregated over active users.
        </p>
        <p>
            Shop time = closed shop-screen flows (client-reported durations,
            built exactly like META time); denominator =
            <code>ctx_total_time_daily_min</code>, the client total-time
            counter. Together with META and HOPA this is the third slice of the
            day - all three use the same denominator and add up to 100%, because
            HOPA is what is left after subtracting these two.
        </p>
        <p>
            <b>Expected to be empty for now</b> - the shop flow events ship with
            client 4.1.100. Until then Shop time is unknown and counted as 0,
            which means those minutes are reported as HOPA. Sample (n) in the
            tooltip shows when players start reporting it.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only).
        </p>`,

    avg_shop_time_share_by_day_no: /* HTML */ `<p>
            Average Shop Daily Time Share: the share of the day spent browsing
            the shop, computed per user on a given day of the player's life and
            averaged over active users - the average counterpart of the median
            chart.
        </p>
        <p>
            Shop time = closed shop-screen flows; denominator =
            <code>ctx_total_time_daily_min</code>, the same client total-time
            counter META Share and HOPA Share use. RAW/uncapped and
            outlier-sensitive - a shop duration longer than the counted day
            pushes a single share above 100% and moves the average more than the
            median.
        </p>
        <p>
            <b>Expected to be empty for now</b> - the shop flow events ship with
            client 4.1.100; until then Shop time is counted as 0 and those
            minutes are reported as HOPA.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only).
        </p>`,

    new_decos_placed_by_active_users_histogram: /* HTML */ `<p>
            Shows how the number of placed decorations is distributed among
            players on the selected day(s).
        </p>
        <p>
            X = NEW decorations placed (first-ever placements; 15+ bucketed
            together), Y = number of players active on that install day; Players
            that did not place any deco form the 0 bucket. One bar series per
            selected day (the "Selected Day" multiselect in the sidebar).
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only).
        </p>`,

    median_daily_inspiration_gain_per_active_user_by_day_no: /* HTML */ `<p>
            Shows the inspiration points players gain on each day of their play.
        </p>
        <p>
            Median across active users of the day's increase of the player's
            inspiration (the maximum ever reached; removing and re-placing a
            decoration does not raise it). 0 on active days with no new gain.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only).
        </p>`,

    avg_daily_inspiration_gain_per_active_user_by_day_no: /* HTML */ `<p>
            Shows the inspiration points players gain on each day of their play
            - average counterpart of the median chart.
        </p>
        <p>
            Daily increase of the inspiration (0 on active days with no new
            gain), averaged across active users.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only).
        </p>`,

    median_career_level_by_day_no: /* HTML */ `<p>
            Shows what META career level players reach on consecutive days of
            their activity.
        </p>
        <p>
            Median across active users of the highest level reached by that day.
            Players with no career signal (pre-4.0.100 builds) drop out - level
            0 is a real value and counts. Sample (n) in the tooltip.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only).
        </p>`,

    avg_career_level_by_day_no: /* HTML */ `<p>
            Shows what META career level players reach on consecutive days of
            their activity - average counterpart of the median chart.
        </p>
        <p>
            Highest level reached by that day, averaged across active users.
            Players with no career signal (pre-4.0.100 builds) drop out - level
            0 is a real value and counts. Sample (n) in the tooltip.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (valid players
            only).
        </p>`,
} as const;
